import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Trans } from "@lingui/macro";

import { Alert } from "components/UI/Alert/Alert";

import { getColor } from "components/utils";
import { Button } from "components/UI/Button/Button";
import {
  IdSalesState,
  typeFullSalesItem,
} from "model/dashboardSales/dSalesReducer";
import { ClipboardListIcon, MinusSmIcon } from "@heroicons/react/outline";
import { ButtonLink } from "components/UI/ButtonLink/ButtonLink";
import { dRefundSetDetailItemFromSalesAC } from "model/dashboardRefunds/dRefundsActionCreators";
import { PopupPanelDashboard } from "components/containers/dashboard/DashboardContentTable/PopupPanelDashboard";
import { PopupDetailsRefund } from "components/containers/dashboard/dataRefunds/popupContents/popupDetails";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { LabelList } from "components/UI/LabelList/LabelList";
import { getTransPaymentType } from "constants/paymentType";
import { Loader } from "components/UI/Loader/Loader";
import { IdRefundsStateDataItem } from "model/dashboardRefunds/dRefundsReducer";
import { getSaleById } from "model/dashboardOrders/dOrdersAPI";
import { formatWithTimezone } from "../../../../../../utils/formatWithTimezone";

// Type
export type typePopupDetails = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
  crossLinkToRefund: boolean;
};

// Component
export const PopupDetails: React.FC<typePopupDetails> = ({
  variant,
  setOpenPopup,
  crossLinkToRefund,
}) => {
  const dispatch = useDispatch();
  const color = getColor(variant);

  // System
  const dataObject = useSelector(
    (state: { dSales: IdSalesState }) => state.dSales.detailItem,
  );

  // get full data with products

  const [dataObjectFull, setDataObjectFull] =
    useState<typeFullSalesItem | null>(null);
  const [loader, setLoader] = useState(false);

  const [vat, setVat] = useState(0);

  useEffect(() => {
    const setData = async () => {
      if (dataObject?.id) {
        setLoader(true);

        const response = await getSaleById({ saleId: dataObject.id });

        if (response.status === 200) {
          const responseData = response.data as typeFullSalesItem;
          setDataObjectFull(response.data);

          let vat = 0;

          const discountPercent =
            dataObject!.discountAmount > 0
              ? dataObject!.discountAmount / responseData.amount
              : 0;

          vat = responseData.productList.reduce((acc, curr) => {
            const currVat =
              curr.tax !== null
                ? curr.tax
                : curr.product?.vat
                  ? curr.product.vat
                  : 0;
            const currPrice =
              (curr.customTotal ? curr.customTotal / curr.amount : curr.price) *
              (1 - discountPercent);
            const vatForItem = (currVat * currPrice) / (1 + currVat);
            return acc + vatForItem * curr.amount;
          }, 0);
          setVat(vat);
        }
      }
    };
    setData().finally(() => {
      setLoader(false);
    });
    // eslint-disable-next-line
  }, []);

  //PopupDetails for Refund
  const [openPopupDetailsRefund, setOpenPopupDetailsRefund] =
    React.useState(false);

  const btnLinkClickHandler = async (
    e: React.MouseEvent | React.TouchEvent,
    id: string,
  ) => {
    if (dataObjectFull?.refunds) {
      const item = dataObjectFull.refunds.find(
        (item: IdRefundsStateDataItem) => item.id === id,
      );
      dispatch(dRefundSetDetailItemFromSalesAC(item));
      setOpenPopupDetailsRefund(true);
    }
  };

  const payments = dataObject!.payments.reduce(
    (acc, cur) => acc + cur.paymentAmount,
    0,
  );

  const rrnArr = dataObjectFull?.payments
    .filter((item) => item.paymentInfoDto?.rrn)
    .map((item) => item.paymentInfoDto?.rrn);

  return (
    <>
      {dataObject ? (
        <>
          <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:col-span-2 sm:border-t-0 border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Sales ID:</Trans>
              </div>
              <div className="text-gray-900 text-left ">
                {dataObject?.salePublicId ? (
                  dataObject.salePublicId
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:border-t-0 border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Receipt number:</Trans>
              </div>
              <div className="text-gray-900 text-left ">
                {dataObject?.receiptNumber ? (
                  dataObject.receiptNumber
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Store name</Trans>:
              </div>
              <div className="text-gray-900 text-left ">
                {dataObjectFull?.store?.name ? (
                  dataObjectFull?.store.name
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 border-t-0   border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Date:</Trans>
              </div>
              <div className="text-gray-900 text-left ">
                {dataObject.date ? formatWithTimezone(dataObject?.date) : "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Address</Trans>:
              </div>
              <div className="text-gray-900 text-left ">
                {dataObjectFull?.store?.address ? (
                  dataObjectFull.store.address
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2   border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Discount amount:</Trans>
              </div>
              <div className="text-gray-900 text-left">
                {dataObject?.discountAmount || 0}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  border-b  ">
              <div className="text-gray-500 text-left">
                <Trans>Manager</Trans>:
              </div>
              <div className="text-gray-90 text-left">
                {dataObjectFull?.store?.manager ? (
                  dataObjectFull.store.manager
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2   border-b sm:border-b-0 ">
              <div className="text-gray-500 text-left">
                <Trans>Payment:</Trans>
              </div>
              <div className="text-gray-500 text-left ">
                {dataObject.fiscalSign ? (
                  +(
                    dataObject.amount -
                    (payments + dataObject.discountAmount)
                  ).toFixed(2) > 0 ? (
                    <p>
                      <span className="text-xs mr-2">
                        <Trans>credit</Trans>:
                      </span>
                      <span className="underline text-xs">
                        <Trans>received</Trans>:
                      </span>
                    </p>
                  ) : (
                    <span className="text-xs">
                      <Trans>full payment</Trans>:
                    </span>
                  )
                ) : (
                  <p>
                    <span className="text-xs mr-2">
                      <Trans>prepayment</Trans>:{" "}
                    </span>
                    <span className="underline text-xs">
                      <Trans>received</Trans>:
                    </span>
                  </p>
                )}
              </div>
              <div className="text-gray-900 text-left ">
                <div className="flex flex-col justify-center divide-y">
                  {dataObject.payments.length ? (
                    dataObject.payments.map((item, index) => {
                      let paymentSystemName =
                        item.paymentInfo?.paymentSystemName;
                      if (!paymentSystemName) {
                        paymentSystemName =
                          item.paymentInfoDto?.paymentSystemName;
                      }

                      return (
                        <div key={index}>
                          <p className="">
                            <span className="text-gray-900">
                              {getTransPaymentType(item.paymentType)}
                              {paymentSystemName
                                ? ` (${paymentSystemName})`
                                : ""}
                              :
                            </span>
                            <span className="text-gray-900 ">
                              {" "}
                              {numberCurrencyFormat(item.paymentAmount)}
                            </span>
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex ">
                      <MinusSmIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              <div className="text-gray-500 text-left ">
                {!dataObject.fiscalSign ||
                +(
                  dataObject.amount -
                  payments -
                  dataObject.discountAmount
                ).toFixed(2) > 0 ? (
                  <p>
                    <span className="underline text-xs mr-2">
                      {" "}
                      <Trans>rest</Trans>:
                    </span>{" "}
                    <span className="text-sm">
                      {numberCurrencyFormat(
                        +(
                          dataObject.amount -
                          payments -
                          dataObject.discountAmount
                        ).toFixed(2),
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>

            {rrnArr && rrnArr.length > 0 && (
              <div className="flex flex-wrap gap-x-4 py-2">
                <div className="text-gray-500 text-left">
                  <Trans>RRN</Trans>:
                </div>
                <div className="text-gray-90 text-left">
                  {rrnArr.join(", ")}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 py-2  sm:justify-between items-baseline relative">
              {!!dataObjectFull?.refunds.length && (
                <>
                  <div className="text-gray-500 text-left">
                    <Trans>The number of refunds</Trans>:
                  </div>
                  <div className="text-gray-900 text-center">
                    {dataObjectFull.refunds.length}
                  </div>
                  <div className="flex flex-col">
                    {crossLinkToRefund &&
                      dataObjectFull.refunds.map((refund, index) => (
                        <ButtonLink
                          type="button"
                          key={refund.id}
                          onClick={(e) => {
                            btnLinkClickHandler(e, refund.id);
                          }}
                          buttonClassName={"py-0 px-2  "}
                          label={
                            <Trans>
                              Refund info <b>{index + 1}</b>
                            </Trans>
                          }
                        />
                      ))}
                  </div>
                </>
              )}
              {loader && <Loader />}
            </div>
          </div>

          <div className="mt-5 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-b ">
              <div className="text-gray-500 text-left">
                <Trans>Cash desk number</Trans>:
              </div>
              <div className="text-gray-900 text-left ">
                {dataObjectFull?.pos?.posNumber ? (
                  dataObjectFull.pos.posNumber
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  border-t border-b sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Applet version: </Trans>
              </div>
              <div className="text-gray-900 text-left">
                {dataObject.appletVersion ? (
                  dataObject.appletVersion
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <div className="flex flex-wrap gap-x-4 py-2  border-b ">
                <div className="text-gray-500 text-left">
                  <Trans>Fiscal card ID</Trans>:
                </div>
                <div className="text-gray-900 text-left ">
                  {dataObjectFull?.pos?.fiscalCardId ? (
                    dataObjectFull.pos.fiscalCardId
                  ) : (
                    <div className="flex ">
                      <MinusSmIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <div className="flex flex-wrap gap-x-4 py-2 border-b  ">
                <div className="flex gap-x-2 items-center text-gray-500 text-left">
                  <Trans>Fiscal module ID: </Trans>
                </div>
                <div className="text-gray-900 text-left">
                  {dataObjectFull?.pos.fiscalCardId ? (
                    dataObjectFull.pos.fiscalCardId
                  ) : (
                    <div className="flex ">
                      <MinusSmIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 py-2 ">
              <div className="text-gray-500 text-left">
                <Trans>Model</Trans>:
              </div>
              <div className="text-gray-900 text-left ">
                {dataObjectFull?.pos?.posHardwareModel ? (
                  dataObjectFull.pos.posHardwareModel
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            {/*{process.env.REACT_APP_CUSTOMER !== 'SKAPPSK' &&  <div className="flex flex-wrap gap-x-4 py-2  border-b ">*/}
            {/*    <div className="flex gap-x-2 items-center text-gray-500 text-left">*/}
            {/*        <Trans>Fiscal sign: </Trans>*/}
            {/*    </div>*/}
            {/*    <div*/}
            {/*        className=" text-left text-gray-900 ">{ dataObject.fiscalSign ? dataObject.fiscalSign :*/}
            {/*        <div className="flex "><MinusSmIcon className="h-5 w-5 text-gray-400"/></div> }</div>*/}
            {/*</div> }*/}
            <div className="flex flex-wrap gap-x-4 py-2  border-b sm:border-0">
              <div className="text-gray-500 text-left">
                <Trans>Serial number</Trans>:
              </div>
              <div className="text-gray-900 text-left ">
                {dataObjectFull?.pos?.posHardwareSerialNumber ? (
                  dataObjectFull.pos.posHardwareSerialNumber
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2  border-b sm:border-b-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>User name: </Trans>
              </div>
              <div className=" text-left text-gray-900 ">
                {dataObject.userName ? (
                  dataObject.userName
                ) : (
                  <div className="flex ">
                    <MinusSmIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-2.5 py-1.5  grid gap-x-8 grid-cols-1  sm:border sm:rounded-md sm:px-4 overflow-x-auto relative">
            <div className="flex justify-between font-medium py-1 mb-1.5">
              <div className="flex gap-x-1  ">
                <span className="text-gray-400">
                  <ClipboardListIcon className="h-6 w-6" />
                </span>
                <span>
                  <Trans>Product list</Trans>
                </span>
              </div>
              <div className={"flex justify-between font-normal"}>
                <div className={"text-gray-700"}>
                  <Trans>Number of products</Trans>:
                </div>
                <div className={"text-gray-900 px-2"}>
                  {dataObjectFull?.productList.length}
                </div>
              </div>
            </div>
            {loader && <Loader />}
            <table className="min-w-full divide-y divide-gray-200 border-t">
              <thead className="bg-gray-50">
                <tr className="relative">
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  ></th>
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Product ID</Trans>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider "
                  >
                    <Trans>Name</Trans>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider "
                  >
                    <Trans>Label</Trans>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider "
                  >
                    <Trans>Count</Trans>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <Trans>Price for unit</Trans>
                  </th>
                  {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
                    <th
                      scope="col"
                      className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      <Trans>VAT</Trans>
                    </th>
                  )}
                  <th
                    scope="col"
                    className="px-3 py-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider "
                  >
                    <Trans>Total price</Trans>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dataObjectFull?.productList.length ? (
                  dataObjectFull.productList.map((item, index) => {
                    let tax;
                    if ("tax" in item && item.tax !== null) {
                      tax = item.tax;
                    } else if ("vat" in item.product) {
                      tax = item.product.vat;
                    } else {
                      tax = 0;
                    }
                    return (
                      <tr
                        key={index}
                        className={` bg-white hover:bg-${color}-50  align-top`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-xs  text-gray-500 ">
                          {index + 1}
                        </td>

                        <td className="px-3 py-2 whitespace-nowrap text-xs  text-gray-500 ">
                          {item.product?.id === "-5" ? "" : item.product?.id}
                        </td>
                        <td className="px-3 py-2  text-sm   text-gray-900 ">
                          {" "}
                          {item.product?.name === "ServicePayment" ? (
                            <Trans>Service payment</Trans>
                          ) : (
                            item.productName || item.product?.name
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm   text-gray-500 ">
                          {!!item?.markedLabels.length && (
                            <LabelList labelsList={item.markedLabels} />
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm  text-gray-500 ">
                          {item?.amount ? item.amount : " - "}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm  text-gray-500 ">
                          {item?.price
                            ? numberCurrencyFormat(item.price)
                            : " - "}
                        </td>
                        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
                          <td className="px-3 py-2 whitespace-nowrap text-sm  text-gray-500 ">
                            {(tax * 100).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}
                            %
                          </td>
                        )}
                        <td className="px-3 py-2 whitespace-nowrap text-sm  text-gray-500 ">
                          {numberCurrencyFormat(
                            item.customTotal
                              ? item.customTotal
                              : +(item.amount * item.price).toFixed(2),
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-3 py-2  whitespace-nowrap text-sm  text-gray-500 col-span-9">
                      there is no products
                    </td>
                  </tr>
                )}

                <tr>
                  <td
                    colSpan={
                      process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? 6 : 7
                    }
                    className="px-3 py-2 whitespace-nowrap text-xs  text-gray-500 col-span-4 text-left"
                  >
                    <Trans>Full cost</Trans>:
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm   text-gray-900 ">
                    {" "}
                    {numberCurrencyFormat(+dataObject.amount.toFixed(2))}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={
                      process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? 6 : 7
                    }
                    className="px-3 py-2 whitespace-nowrap text-xs  text-gray-500 col-span-4 text-left"
                  >
                    <Trans>Discount amount</Trans>:
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm   text-gray-500 ">
                    {" "}
                    {numberCurrencyFormat(dataObject.discountAmount)}
                  </td>
                </tr>
                {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-2 whitespace-nowrap text-xs  text-gray-500 col-span-4 text-left"
                    >
                      <Trans>VAT including</Trans>:
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm   text-gray-500 ">
                      {" "}
                      {numberCurrencyFormat(vat)}
                    </td>
                  </tr>
                )}

                <tr>
                  <td
                    colSpan={
                      process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? 6 : 7
                    }
                    className="px-3 py-2 whitespace-nowrap text-xs  text-gray-900 col-span-4 text-left"
                  >
                    <Trans>Total</Trans>:
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm   text-gray-900 ">
                    {" "}
                    {numberCurrencyFormat(
                      dataObject.amount - dataObject.discountAmount,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <Button
              type="button"
              secondary={true}
              label={<Trans>Close</Trans>}
              onClick={() => setOpenPopup(false)}
            />
          </div>
        </>
      ) : (
        <Alert type="error" title={<Trans>Have no data.</Trans>} />
      )}
      {/* Popup Details Refunds dialog */}
      <PopupPanelDashboard
        title={<Trans>Fool refund information</Trans>}
        openPopup={openPopupDetailsRefund}
        setOpenPopup={setOpenPopupDetailsRefund}
      >
        <PopupDetailsRefund
          setOpenPopup={setOpenPopupDetailsRefund}
          crossLinkToSale={false}
        />
      </PopupPanelDashboard>
    </>
  );
};
